import { apiClient, weComSyncApiClient } from '@/api'

export const useWeComStore = defineStore('weCom', () => {
  const departments = ref<Awaited<ReturnType<typeof apiClient.wecom.department.list>>>([])
  const employees = ref<Awaited<ReturnType<typeof apiClient.wecom.employee.list>> | null>(null)
  const customerGroups = ref<Awaited<ReturnType<typeof apiClient.wecom.customerGroup.list>> | null>(null)
  const currentCustomerGroup = ref<Awaited<ReturnType<typeof apiClient.wecom.customerGroup.get>> | null>(null)
  const externalContacts = ref<Awaited<ReturnType<typeof apiClient.wecom.externalContact.list>> | null>(null)
  const currentExternalContact = ref<Awaited<ReturnType<typeof apiClient.wecom.externalContact.get>> | null>(null)
  const externalContactTags = ref<Awaited<ReturnType<typeof apiClient.wecom.externalContact.tag.list>>>([])
  let departmentGeneration = 0
  let employeeGeneration = 0
  let customerGroupGeneration = 0
  let externalContactGeneration = 0

  async function loadDepartments(includeInactive = false) {
    const request = ++departmentGeneration
    const result = await apiClient.wecom.department.list({ includeInactive })
    if (request === departmentGeneration) {
      departments.value = result
    }
    return result
  }

  async function loadEmployees(input: Parameters<typeof apiClient.wecom.employee.list>[0]) {
    const request = ++employeeGeneration
    const result = await apiClient.wecom.employee.list(input)
    if (request === employeeGeneration) {
      employees.value = result
    }
    return result
  }

  async function setAdminLoginEnabled(input: Parameters<typeof apiClient.wecom.employee.setAdminLoginEnabled>[0]) {
    const employee = await apiClient.wecom.employee.setAdminLoginEnabled(input)
    if (employees.value) {
      const row = employees.value.items.find(item => item.userId === employee.userId)
      if (row) {
        row.adminLoginEnabled = employee.adminLoginEnabled
      }
    }
    return employee
  }

  async function loadCustomerGroups(input: Parameters<typeof apiClient.wecom.customerGroup.list>[0]) {
    const request = ++customerGroupGeneration
    const result = await apiClient.wecom.customerGroup.list(input)
    if (request === customerGroupGeneration) {
      customerGroups.value = result
    }
    return result
  }

  async function loadCustomerGroup(chatId: string) {
    currentCustomerGroup.value = await apiClient.wecom.customerGroup.get({ chatId })
    return currentCustomerGroup.value
  }

  async function loadExternalContacts(input: Parameters<typeof apiClient.wecom.externalContact.list>[0]) {
    const request = ++externalContactGeneration
    const result = await apiClient.wecom.externalContact.list(input)
    if (request === externalContactGeneration) {
      externalContacts.value = result
    }
    return result
  }

  async function loadExternalContact(externalUserId: string) {
    currentExternalContact.value = await apiClient.wecom.externalContact.get({ externalUserId })
    return currentExternalContact.value
  }

  async function loadExternalContactTags() {
    externalContactTags.value = await apiClient.wecom.externalContact.tag.list()
    return externalContactTags.value
  }

  function clearCustomerGroup() {
    currentCustomerGroup.value = null
  }

  function clearExternalContact() {
    currentExternalContact.value = null
  }

  function reset() {
    departmentGeneration += 1
    employeeGeneration += 1
    customerGroupGeneration += 1
    externalContactGeneration += 1
    departments.value = []
    employees.value = null
    customerGroups.value = null
    currentCustomerGroup.value = null
    externalContacts.value = null
    currentExternalContact.value = null
    externalContactTags.value = []
  }

  return {
    clearCustomerGroup,
    clearExternalContact,
    currentCustomerGroup,
    currentExternalContact,
    customerGroups,
    externalContacts,
    externalContactTags,
    departments,
    employees,
    loadCustomerGroup,
    loadCustomerGroups,
    loadDepartments,
    loadEmployees,
    loadExternalContact,
    loadExternalContacts,
    loadExternalContactTags,
    reset,
    setAdminLoginEnabled,
    syncCustomerGroups: () => weComSyncApiClient.wecom.customerGroup.sync(),
    syncDirectory: () => weComSyncApiClient.wecom.directory.sync(),
    syncExternalContacts: () => weComSyncApiClient.wecom.externalContact.sync(),
  }
})
